import {
  AI_SPEND_INCLUSION_TYPES,
  EXPORT_FIELDS_INCLUSION_TYPES,
  EXPORT_INCLUSION_TYPES,
  type ExportFieldConfiguration,
  type ExportPayload,
} from "@/components/Export/types";
import type {
  CompanyFilter as LegacyCompanyFilter,
  ContactsFilter,
} from "@/legacy/types/companyFilter";
import type { IntentSignalScoreUi } from "@/components/Export/components/IntentThresholdSelector";
import type { FinancingEventsFilters } from "@/components/Export/financingEvents";
import type { MergersAcquisitionsFilters } from "@/components/Export/mergersAcquisitions";
import { toOppGenFilterShape } from "./oppGenFilterShape";

export interface AIScoringSettings {
  tenantId?: string;
  modelSelection: "selected" | "none";
  modelFamilies: string[];
  tiers: string[];
}

export interface ContactsFacExportSettings {
  inclusion: EXPORT_INCLUSION_TYPES;
  productIds: number[];
  configuration: ExportFieldConfiguration;
  contactsPerCompanyLimit: number | null;
}

/**
 * Inputs for the Universe JSON — the same set OppGen's `buildExportPayload`
 * (piq-customer-ui `exportDialog/utils/exportPayloadBuilder.ts`) takes, so the
 * two apps emit the same document for the same selection. Keep the two in
 * step: covered-wagon parses this JSON verbatim (`OppGenQueries.ExportConfig`).
 */
export interface UniversePayloadBuilderProps {
  /** The classic (XLSX) payload; its searchParams/columns seed the JSON. */
  exportPayload: ExportPayload;
  /** Overseer org id, stamped on `companyMatch` for covered-wagon's rematch. */
  orgId?: string | null;
  installInclusion: EXPORT_INCLUSION_TYPES;
  productsIds: number[];
  faiProductIds: number[];
  faiRoleIds: string[];
  faiDepartmentIds: string[];
  decisionMakersAndInfluencersOnly: boolean;
  installConfiguration: ExportFieldConfiguration;
  timeSeriesConfiguration: ExportFieldConfiguration;
  mentionInclusion: EXPORT_INCLUSION_TYPES;
  mentionsIds: number[];
  mentionConfiguration: ExportFieldConfiguration;
  mentionTimeSeriesConfiguration: ExportFieldConfiguration;
  generativeAIInclusion: EXPORT_INCLUSION_TYPES;
  generativeAIConfiguration: ExportFieldConfiguration;
  intentInclusion: EXPORT_INCLUSION_TYPES;
  topicIds: string[];
  intentCollectionIds: string[];
  /**
   * The collection picked in the sidebar Intent filter. OppGen sends it as
   * `intent.topics.collectionIds`; covered-wagon falls back to it for the
   * intent file's INTENT_COLLECTION column.
   */
  filterIntentCollectionId?: string | null;
  intentSignalScore: IntentSignalScoreUi;
  intentConfiguration: ExportFieldConfiguration;
  contractConfiguration: ExportFieldConfiguration;
  spendConfiguration: ExportFieldConfiguration;
  relativeSpendConfiguration: ExportFieldConfiguration;
  rowBasedSpendConfiguration: ExportFieldConfiguration;
  faiConfiguration: ExportFieldConfiguration;
  exportInstallLookback: number | string;
  exportMentionLookback: number | string;
  exportTimeSeriesLookback: number | string;
  exportCompaniesInstallLookback: number | string;
  includeSMBSpend: boolean;
  includeSMBRelativeSpend: boolean;
  includeSMBRowBasedSpend: boolean;
  buyerIntentInclusion: EXPORT_INCLUSION_TYPES;
  buyerIntentProductIds: number[];
  contactsInclusion: EXPORT_INCLUSION_TYPES;
  contactsConfiguration: ExportFieldConfiguration;
  contactFilters?: ContactsFilter;
  contactsPerCompanyLimit?: number | null;
  contactsFac?: ContactsFacExportSettings;
  financingEventsInclusion?: EXPORT_INCLUSION_TYPES;
  financingEventsConfiguration?: ExportFieldConfiguration;
  financingEventsFilters?: FinancingEventsFilters;
  mergersAcquisitionsInclusion?: EXPORT_INCLUSION_TYPES;
  mergersAcquisitionsConfiguration?: ExportFieldConfiguration;
  mergersAcquisitionsFilters?: MergersAcquisitionsFilters;
  aiSpendInclusion: AI_SPEND_INCLUSION_TYPES;
  absoluteAISpendConfiguration: ExportFieldConfiguration;
  relativeAISpendConfiguration: ExportFieldConfiguration;
  includeSMBAISpend: boolean;
  aiScoring?: AIScoringSettings;
}

// OppGen's `removeExtraProductProperties`: product filters keep only what the
// backend reads (`type`/`collectionId` are UI bookkeeping).
function removeExtraProductProperties(
  filters: LegacyCompanyFilter
): LegacyCompanyFilter {
  return {
    ...filters,
    products: filters.products?.map((productFilter) => {
      const {
        type: _type,
        collectionId: _collectionId,
        ...rest
      } = productFilter as typeof productFilter & {
        type?: unknown;
        collectionId?: unknown;
      };
      return rest;
    }),
  };
}

function withFilterIntentCollection(
  filters: LegacyCompanyFilter,
  collectionId: string | null | undefined
): LegacyCompanyFilter {
  const topics = filters.intent?.topics;
  if (!collectionId || !topics?.ids?.length) return filters;
  return {
    ...filters,
    intent: {
      ...filters.intent,
      topics: { ...topics, collectionIds: [collectionId] },
    },
  };
}

function profileWithoutOrgId(
  profile: ExportPayload["profile"] | null | undefined
): Record<string, unknown> | null {
  if (!profile) return null;
  const { orgId: _orgId, ...rest } = profile as typeof profile & {
    orgId?: unknown;
  };
  return rest;
}

export function buildUniversePayload(props: UniversePayloadBuilderProps) {
  const { exportPayload } = props;
  const { params } = exportPayload;

  const {
    installConfiguration,
    timeSeriesConfiguration,
    mentionConfiguration,
    mentionInclusion,
    mentionTimeSeriesConfiguration,
    mentionsIds,
    faiProductIds,
    faiRoleIds,
    faiDepartmentIds,
    decisionMakersAndInfluencersOnly,
    generativeAIConfiguration,
    generativeAIInclusion,
    intentConfiguration,
    contractConfiguration,
    spendConfiguration,
    relativeSpendConfiguration,
    rowBasedSpendConfiguration,
    faiConfiguration,
    installInclusion,
    intentInclusion,
    productsIds,
    topicIds,
    intentCollectionIds,
    intentSignalScore,
    exportInstallLookback,
    exportMentionLookback,
    exportTimeSeriesLookback,
    exportCompaniesInstallLookback,
    includeSMBSpend,
    includeSMBRelativeSpend,
    includeSMBRowBasedSpend,
    buyerIntentInclusion,
    buyerIntentProductIds,
    contactsInclusion,
    contactsConfiguration,
    aiSpendInclusion,
    absoluteAISpendConfiguration,
    relativeAISpendConfiguration,
    includeSMBAISpend,
  } = props;

  const relativeSpendRangeOption =
    params.relativeSpendInclusion === EXPORT_INCLUSION_TYPES.FILTERS
      ? { label: "it_budget_total", value: { min: 0 } }
      : { label: "", value: {} };

  const baseFilters = withFilterIntentCollection(
    removeExtraProductProperties(
      toOppGenFilterShape(params.searchParams.filters)
    ),
    props.filterIntentCollectionId
  );

  const filtersWithOrgId: LegacyCompanyFilter =
    props.orgId && baseFilters.companyMatch
      ? {
          ...baseFilters,
          companyMatch: { ...baseFilters.companyMatch, orgId: props.orgId },
        }
      : baseFilters;

  return {
    params: {
      searchParams: {
        ...params.searchParams,
        filters: filtersWithOrgId,
        installAge: exportCompaniesInstallLookback,
        exportInstallLookback,
        exportMentionLookback,
      },
      isCloudMaturityEnabled: params.isCloudMaturityEnabled,
      exports: [
        {
          file_type: "installs",
          inclusionType: installInclusion,
          fieldSelection: installConfiguration.inclusionType,
          fields: installConfiguration.fields,
          firmoFields: installConfiguration.firmographicsFields,
          firmoFieldSelection: installConfiguration.firmographicsInclusionType,
          productIds: productsIds,
        },
        {
          file_type: "time_series",
          inclusionType: params.timeSeriesInclusion,
          fieldSelection: timeSeriesConfiguration.inclusionType,
          fields: timeSeriesConfiguration.fields,
          timeSeriesLookback: exportTimeSeriesLookback,
        },
        {
          file_type: "mentions",
          inclusionType: mentionInclusion,
          fieldSelection: mentionConfiguration.inclusionType,
          fields: mentionConfiguration.fields,
          firmoFields: mentionConfiguration.firmographicsFields,
          firmoFieldSelection: mentionConfiguration.firmographicsInclusionType,
          productIds: mentionsIds,
        },
        {
          file_type: "generative_ai",
          inclusionType: generativeAIInclusion,
          fieldSelection: generativeAIConfiguration.inclusionType,
          fields: generativeAIConfiguration.fields,
          firmoFields: generativeAIConfiguration.firmographicsFields,
          firmoFieldSelection:
            generativeAIConfiguration.firmographicsInclusionType,
        },
        {
          file_type: "mention_time_series",
          inclusionType: params.mentionTimeSeriesInclusion,
          fieldSelection: mentionTimeSeriesConfiguration.inclusionType,
          fields: mentionTimeSeriesConfiguration.fields,
        },
        {
          file_type: "contracts",
          inclusionType: params.contractsInclusion,
          fieldSelection: contractConfiguration.inclusionType,
          fields: contractConfiguration.fields,
          firmoFields: contractConfiguration.firmographicsFields,
          firmoFieldSelection: contractConfiguration.firmographicsInclusionType,
        },
        {
          file_type: "spend",
          inclusionType: params.spendInclusion,
          fieldSelection: spendConfiguration.inclusionType,
          fields: spendConfiguration.fields,
          firmoFields: spendConfiguration.firmographicsFields,
          firmoFieldSelection: spendConfiguration.firmographicsInclusionType,
          customColumns: {
            productPresence: params.columns.spend.productPresence,
            vendorPresence: params.columns.spend.vendorPresence,
          },
          includeSMB: includeSMBSpend,
        },
        {
          file_type: "relative_spend",
          inclusionType: params.relativeSpendInclusion,
          fieldSelection: relativeSpendConfiguration.inclusionType,
          fields: relativeSpendConfiguration.fields,
          firmoFields: relativeSpendConfiguration.firmographicsFields,
          firmoFieldSelection:
            relativeSpendConfiguration.firmographicsInclusionType,
          rangeOption: relativeSpendRangeOption,
          includeSMB: includeSMBRelativeSpend,
        },
        {
          file_type: "row_based_spend",
          inclusionType: params.rowBasedSpendInclusion,
          fieldSelection: rowBasedSpendConfiguration.inclusionType,
          fields: rowBasedSpendConfiguration.fields,
          firmoFields: rowBasedSpendConfiguration.firmographicsFields,
          firmoFieldSelection:
            rowBasedSpendConfiguration.firmographicsInclusionType,
          includeSMB: includeSMBRowBasedSpend,
        },
        {
          file_type: "intent",
          inclusionType: intentInclusion,
          fieldSelection: intentConfiguration.inclusionType,
          fields: intentConfiguration.fields,
          firmoFields: intentConfiguration.firmographicsFields,
          firmoFieldSelection: intentConfiguration.firmographicsInclusionType,
          topicIds,
          intentCollectionIds:
            intentCollectionIds.length > 0 ? intentCollectionIds : undefined,
          signalScore: intentSignalScore === "ALL" ? null : intentSignalScore,
        },
        {
          file_type: "fai",
          inclusionType: params.faiInclusion,
          fieldSelection: faiConfiguration.inclusionType,
          fields: faiConfiguration.fields,
          productIds: faiProductIds,
          roleIds: faiRoleIds,
          departmentIds: faiDepartmentIds,
          includeDecisionMakersAndInfluencersOnly:
            decisionMakersAndInfluencersOnly,
        },
        {
          file_type: "buyer_intent",
          inclusionType: buyerIntentInclusion,
          fields: [],
          fieldSelection: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
          firmoFields: [],
          firmoFieldSelection: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
          productIds:
            buyerIntentInclusion === EXPORT_INCLUSION_TYPES.SELECTED
              ? buyerIntentProductIds
              : [],
        },
        ...(props.financingEventsInclusion &&
        props.financingEventsInclusion !== EXPORT_INCLUSION_TYPES.NONE &&
        props.financingEventsConfiguration
          ? [
              {
                file_type: "financing_events" as const,
                inclusionType: props.financingEventsInclusion,
                fieldSelection:
                  props.financingEventsConfiguration.inclusionType,
                fields: props.financingEventsConfiguration.fields,
                firmoFields:
                  props.financingEventsConfiguration.firmographicsFields,
                firmoFieldSelection:
                  props.financingEventsConfiguration.firmographicsInclusionType,
                ...(props.financingEventsFilters
                  ? { filters: props.financingEventsFilters }
                  : {}),
              },
            ]
          : []),
        ...(props.mergersAcquisitionsInclusion &&
        props.mergersAcquisitionsInclusion !== EXPORT_INCLUSION_TYPES.NONE &&
        props.mergersAcquisitionsConfiguration
          ? [
              {
                file_type: "mergers_acquisitions" as const,
                inclusionType: props.mergersAcquisitionsInclusion,
                fieldSelection:
                  props.mergersAcquisitionsConfiguration.inclusionType,
                fields: props.mergersAcquisitionsConfiguration.fields,
                firmoFields:
                  props.mergersAcquisitionsConfiguration.firmographicsFields,
                firmoFieldSelection:
                  props.mergersAcquisitionsConfiguration
                    .firmographicsInclusionType,
                ...(props.mergersAcquisitionsFilters
                  ? { filters: props.mergersAcquisitionsFilters }
                  : {}),
              },
            ]
          : []),
        ...(contactsInclusion !== EXPORT_INCLUSION_TYPES.NONE
          ? [
              {
                file_type: "contacts" as const,
                inclusionType: contactsInclusion,
                fieldSelection: contactsConfiguration.inclusionType,
                fields: contactsConfiguration.fields,
                firmoFields: contactsConfiguration.firmographicsFields,
                firmoFieldSelection:
                  contactsConfiguration.firmographicsInclusionType,
                ...(props.contactFilters
                  ? { filters: props.contactFilters }
                  : {}),
                ...(props.contactsPerCompanyLimit
                  ? { contactsPerCompanyLimit: props.contactsPerCompanyLimit }
                  : {}),
              },
            ]
          : []),
        ...buildContactsFacExport(props.contactsFac, props.contactFilters),
        ...(aiSpendInclusion === AI_SPEND_INCLUSION_TYPES.ABSOLUTE ||
        aiSpendInclusion === AI_SPEND_INCLUSION_TYPES.BOTH
          ? [
              {
                file_type: "ai_spend" as const,
                inclusionType: EXPORT_INCLUSION_TYPES.ALL,
                fieldSelection: absoluteAISpendConfiguration.inclusionType,
                fields: absoluteAISpendConfiguration.fields,
                firmoFields: absoluteAISpendConfiguration.firmographicsFields,
                firmoFieldSelection:
                  absoluteAISpendConfiguration.firmographicsInclusionType,
                includeSMB: includeSMBAISpend,
              },
            ]
          : []),
        ...(aiSpendInclusion === AI_SPEND_INCLUSION_TYPES.RELATIVE ||
        aiSpendInclusion === AI_SPEND_INCLUSION_TYPES.BOTH
          ? [
              {
                file_type: "ai_spend_relative" as const,
                inclusionType: EXPORT_INCLUSION_TYPES.ALL,
                fieldSelection: relativeAISpendConfiguration.inclusionType,
                fields: relativeAISpendConfiguration.fields,
                firmoFields: relativeAISpendConfiguration.firmographicsFields,
                firmoFieldSelection:
                  relativeAISpendConfiguration.firmographicsInclusionType,
                includeSMB: includeSMBAISpend,
              },
            ]
          : []),
      ],
      userProfile: profileWithoutOrgId(exportPayload.profile),
      aiScoring: props.aiScoring,
    },
    scope: exportPayload.scope,
  };
}

function buildContactsFacExport(
  contactsFac: ContactsFacExportSettings | undefined,
  contactFilters: ContactsFilter | undefined
) {
  if (!contactsFac || contactsFac.inclusion === EXPORT_INCLUSION_TYPES.NONE) {
    return [];
  }

  const { inclusion, productIds, configuration, contactsPerCompanyLimit } =
    contactsFac;

  return [
    {
      file_type: "contacts_fac" as const,
      inclusionType: inclusion,
      fieldSelection: configuration.inclusionType,
      fields: configuration.fields,
      firmoFields: configuration.firmographicsFields,
      firmoFieldSelection: configuration.firmographicsInclusionType,
      ...(inclusion === EXPORT_INCLUSION_TYPES.SELECTED ? { productIds } : {}),
      ...(contactFilters ? { filters: contactFilters } : {}),
      ...(contactsPerCompanyLimit ? { contactsPerCompanyLimit } : {}),
    },
  ];
}
